import React, { useState, useEffect } from "react";
import { X, Check, ArrowUpRight } from "lucide-react";
import axios from "axios";
import { CATALOG_URL } from "../constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LeadFormModal({ open, onClose, source = "catalog_request" }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setSuccess(false);
      setErrors({});
    }
  }, [open]);

  // Lock body scroll when open
  useEffect(() => {
    if (!open) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, [open]);

  if (!open) return null;

  const validate = () => {
    const e = {};
    if (!form.name.trim() || form.name.trim().length < 2)
      e.name = "Cuéntanos tu nombre";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Correo inválido";
    if (!/^[\d+\-\s()]{7,}$/.test(form.phone))
      e.phone = "Número inválido";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        source,
      });
      setSuccess(true);
      // Open catalog after a short delay so user sees the success state
      setTimeout(() => {
        window.open(CATALOG_URL, "_blank", "noopener,noreferrer");
      }, 600);
    } catch (err) {
      // Allow them to access the catalog even if backend fails (best UX)
      console.error("Lead submit error", err);
      setSuccess(true);
      setTimeout(() => {
        window.open(CATALOG_URL, "_blank", "noopener,noreferrer");
      }, 600);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="lead-modal"
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-ink/60 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative w-full max-w-md bg-page rounded-3xl border border-line shadow-[0_60px_140px_-30px_rgba(0,0,0,0.45)] overflow-hidden">
        {/* Close */}
        <button
          onClick={onClose}
          aria-label="Cerrar"
          data-testid="lead-modal-close"
          className="absolute top-4 right-4 z-10 inline-flex items-center justify-center w-9 h-9 rounded-full text-ink-soft hover:bg-sand hover:text-ink transition-colors"
        >
          <X size={18} />
        </button>

        {/* Decorative top band */}
        <div className="bg-vino px-8 py-7 text-page">
          <div className="text-[10px] uppercase tracking-[0.3em] text-cream/80 mb-2">
            Catálogo Vallitas
          </div>
          <h2 className="font-heading text-3xl leading-tight">
            {success ? "¡Listo!" : "Casi tienes tu catálogo"}
          </h2>
          <p className="mt-2 text-sm text-cream/90">
            {success
              ? "Te lo enviamos también por WhatsApp si nos escribes."
              : "Déjanos tus datos para enviártelo y mantenerte al día."}
          </p>
        </div>

        {/* Body */}
        <div className="p-8">
          {success ? (
            <div className="text-center py-4">
              <div className="mx-auto mb-5 inline-flex items-center justify-center w-14 h-14 rounded-full bg-vino/10 text-vino">
                <Check size={26} strokeWidth={2.5} />
              </div>
              <p className="font-heading text-xl text-ink mb-2">
                Gracias, {form.name.split(" ")[0]}
              </p>
              <p className="text-sm text-ink-soft mb-6">
                Abrimos tu catálogo en una nueva pestaña.
              </p>
              <a
                href={CATALOG_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="lead-modal-catalog-link"
                className="inline-flex items-center gap-2 rounded-full bg-vino text-page px-6 py-3 text-sm font-medium hover:bg-vino-deep transition-colors"
              >
                Abrir catálogo
                <ArrowUpRight size={16} />
              </a>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              <div>
                <label className="block text-[11px] uppercase tracking-[0.2em] text-ink-soft mb-2">
                  Nombre completo
                </label>
                <input
                  type="text"
                  data-testid="lead-name"
                  value={form.name}
                  onChange={(e) =>
                    setForm({ ...form, name: e.target.value })
                  }
                  className={`w-full px-4 py-3 rounded-2xl bg-sand border text-sm text-ink focus:outline-none transition-colors ${
                    errors.name
                      ? "border-red-500"
                      : "border-line focus:border-vino"
                  }`}
                  placeholder="María Fernández"
                />
                {errors.name && (
                  <p className="mt-1 text-xs text-red-600">{errors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-[11px] uppercase tracking-[0.2em] text-ink-soft mb-2">
                  Correo electrónico
                </label>
                <input
                  type="email"
                  data-testid="lead-email"
                  value={form.email}
                  onChange={(e) =>
                    setForm({ ...form, email: e.target.value })
                  }
                  className={`w-full px-4 py-3 rounded-2xl bg-sand border text-sm text-ink focus:outline-none transition-colors ${
                    errors.email
                      ? "border-red-500"
                      : "border-line focus:border-vino"
                  }`}
                  placeholder="maria@correo.com"
                />
                {errors.email && (
                  <p className="mt-1 text-xs text-red-600">{errors.email}</p>
                )}
              </div>

              <div>
                <label className="block text-[11px] uppercase tracking-[0.2em] text-ink-soft mb-2">
                  Teléfono / WhatsApp
                </label>
                <input
                  type="tel"
                  data-testid="lead-phone"
                  value={form.phone}
                  onChange={(e) =>
                    setForm({ ...form, phone: e.target.value })
                  }
                  className={`w-full px-4 py-3 rounded-2xl bg-sand border text-sm text-ink focus:outline-none transition-colors ${
                    errors.phone
                      ? "border-red-500"
                      : "border-line focus:border-vino"
                  }`}
                  placeholder="+58 414 1234567"
                />
                {errors.phone && (
                  <p className="mt-1 text-xs text-red-600">{errors.phone}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="lead-submit"
                className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-full bg-vino text-page py-4 text-sm font-medium hover:bg-vino-deep transition-all hover:-translate-y-0.5 shadow-[0_18px_40px_-15px_rgba(114,47,55,0.45)] disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? "Enviando..." : "Recibir el catálogo"}
                {!submitting && <ArrowUpRight size={16} />}
              </button>

              <p className="text-[11px] text-ink-soft text-center">
                Tus datos se usan solo para contactarte sobre Vallitas. No
                spam.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
